import { memo, useState } from "react";
import type { FileManagerRevealKind } from "@cmmd-forge/contracts";

import { cn } from "~/lib/utils";
import type { MarkdownFileLinkMeta } from "~/markdown-links";
import { getVscodeIconUrlForEntry, inferEntryKindFromPath } from "~/vscode-icons";
import { Tooltip, TooltipPopup, TooltipTrigger } from "../ui/tooltip";
import { Menu, MenuItem, MenuPopup } from "../ui/menu";

/**
 * A file reference in a message, rendered as a chip that opens the file.
 *
 * The label is the basename plus, when the reference carries one, the position
 * (`main.ts · L42`). The full path lives in the tooltip rather than the chip:
 * agents quote deep paths constantly and a chip wide enough for
 * `apps/web/src/components/chat/…` reflows the surrounding sentence.
 *
 * `data-markdown-copy` carries the original markdown so copying a selection out
 * of the transcript yields the source text rather than the chip's label —
 * `chatMarkdownClipboardPayload` reads that attribute.
 */
interface MarkdownFileLinkProps {
  readonly meta: MarkdownFileLinkMeta;
  readonly copyMarkdown: string;
  readonly theme: "light" | "dark";
  readonly onOpen: (meta: MarkdownFileLinkMeta) => void;
  readonly revealKind?: FileManagerRevealKind | undefined;
  readonly className?: string | undefined;
}

function buildFileLinkLabel(meta: MarkdownFileLinkMeta): string {
  if (!meta.line) return meta.basename;
  return `${meta.basename} · L${meta.line}${meta.column ? `:C${meta.column}` : ""}`;
}

export const MarkdownFileLink = memo(function MarkdownFileLink({
  meta,
  copyMarkdown,
  theme,
  onOpen,
  revealKind,
  className,
}: MarkdownFileLinkProps) {
  const [menuPoint, setMenuPoint] = useState<{ readonly x: number; readonly y: number } | null>(
    null,
  );
  const revealLabel = fileManagerRevealLabel(revealKind);
  return (
    <>
      <Tooltip>
        <TooltipTrigger
          render={
            <a
              href={meta.targetPath}
              data-markdown-file-link={meta.filePath}
              data-markdown-copy={copyMarkdown}
              className={cn(
                // Geometry only. Colour, underline and hover live in `index.css`
                // beside the other `.chat-markdown` rules, because the anchor
                // rule there outranks any single utility class here.
                "inline-flex max-w-full min-w-0 items-baseline gap-1 rounded-md border border-border bg-muted px-1.5 py-[0.1rem] align-baseline font-mono text-[var(--chat-markdown-code-font-size)] leading-[1.45] transition-colors",
                className,
              )}
              onClick={(event) => {
                // The transcript row is itself clickable in places; a chip click
                // means "open this file" and nothing else.
                event.preventDefault();
                event.stopPropagation();
                onOpen(meta);
              }}
              onContextMenu={(event) => {
                event.preventDefault();
                event.stopPropagation();
                setMenuPoint({ x: event.clientX, y: event.clientY });
              }}
            >
              <img
                src={getVscodeIconUrlForEntry(
                  meta.filePath,
                  inferEntryKindFromPath(meta.filePath),
                  theme,
                )}
                alt=""
                aria-hidden="true"
                className="size-3.5 shrink-0 self-center"
              />
              <span className="truncate">{buildFileLinkLabel(meta)}</span>
            </a>
          }
        />
        <TooltipPopup side="top" className="font-mono text-[11px]">
          {meta.displayPath}
        </TooltipPopup>
      </Tooltip>
      <Menu open={menuPoint !== null} onOpenChange={(open) => !open && setMenuPoint(null)}>
        <MenuPopup
          anchor={
            menuPoint
              ? {
                  getBoundingClientRect: () => new DOMRect(menuPoint.x, menuPoint.y, 0, 0),
                }
              : undefined
          }
          side="bottom"
          align="start"
        >
          <MenuItem
            onClick={() => {
              onOpen(meta);
              setMenuPoint(null);
            }}
          >
            {revealLabel}
          </MenuItem>
          <MenuItem
            onClick={() => {
              void navigator.clipboard.writeText(meta.workspaceRelativePath ?? meta.filePath);
              setMenuPoint(null);
            }}
          >
            Copy path
          </MenuItem>
        </MenuPopup>
      </Menu>
    </>
  );
});

function fileManagerRevealLabel(kind: FileManagerRevealKind | undefined): string {
  if (kind === "finder") return "Reveal in Finder";
  if (kind === "file-explorer") return "Show in File Explorer";
  if (kind === "files") return "Show in Files";
  return "Show in file manager";
}
